import * as Sentry from '@sentry/browser'
import firebase from 'firebase/compat/app'
import 'firebase/compat/functions'
import {
  addInvitations,
  updateInvitations,
  sendInvites
} from '@/services/invitations-api.service.js'
import {
  createPlanInvitationForOutsider,
  sendPlanInvitationEmail
} from '@/services/plan-invitations-api.service.js'
import { logger } from '@/utils/logger'

/**
 * Encapsulates the logic for sending a board invite - add invitation to DB and send email
 * @param {string} boardId
 * @param {Array} invites - object array, required fields
 *  {
 *    boardId,
 *    invitedBy,
 *    inviterEmail,
 *    recipientEmail,
 *    recipientName,
 *    boardName,
 *    accessLevel,
 *    isReminder,
 *    sendEmail = true
 *  }
 */
export async function sendBoardInvites(boardId, invites = []) {
  // Sanity checks
  if (!Array.isArray(invites)) {
    throw new TypeError('Expected an array of invite-objects')
  }

  if (invites.some((invite) => invite.boardId !== boardId)) {
    throw new Error('All invites must have the same boardId')
  }

  const requiredKeys = [
    'boardName',
    'boardId',
    'invitedBy',
    'inviterEmail',
    'recipientEmail',
    'recipientName',
    'accessLevel',
    'isReminder',
    'sendEmail'
  ]

  invites.forEach((invite) => {
    if (!requiredKeys.every((key) => key in invite)) {
      throw new Error('Sanity check failed: missing one or more required keys')
    }
  })

  try {
    const origin = window.location.origin

    // Remove existing invites and record new access level for audit logging (group by accessLevel)
    const recipientsByAccessLevel = invites.reduce((acc, invite) => {
      const level = invite.accessLevel
      if (!acc[level]) acc[level] = []
      acc[level].push(invite.recipientEmail)
      return acc
    }, {})

    for (const [accessLevel, recipientEmails] of Object.entries(recipientsByAccessLevel)) {
      await updateInvitations({
        boardId,
        recipientEmails,
        patch: {
          ignore: true,
          accessLevel
        }
      })
    }

    const invitationsToSend = invites
      .filter((invite) => invite.sendEmail)
      .map((invite) => {
        const template_id = invite.isReminder
          ? 'd-77d1d25cb3b54734bd7589fb99e17f71'
          : 'd-fa518f0c3eef405495fe2802bb7c7933'

        return {
          origin,
          boardName: invite.boardName,
          invitedBy: invite.invitedBy,
          recipientEmail: invite.recipientEmail,
          recipientName: invite.recipientName,
          template_id,
          boardId: invite.boardId,
          accessLevel: invite.accessLevel
        }
      })

    if (invitationsToSend.length > 0) {
      await sendInvites(boardId, invitationsToSend)
    }

    await addInvitations({
      boardId,
      invitations: invites.map((invite) => ({
        accessLevel: invite.accessLevel,
        boardId: invite.boardId,
        boardName: invite.boardName,
        invitationAccepted: false,
        invitedBy: invite.invitedBy,
        inviterEmail: invite.inviterEmail,
        recipient: invite.recipientEmail,
        recipientName: invite.recipientName
      }))
    })
    window.mixpanel.track('invite_others')
    return true
  } catch (error) {
    logger.error(error.message)
    Sentry?.captureException(error)
    return false
  }
}

/**
 * Encapsulates the logic for sending a plan invite email
 */
export async function sendPlanInviteEmail({ boardName, boardId, invitedBy, recipients, origin }) {
  try {
    await sendPlanInvitationEmail({
      boardName,
      boardId,
      recipients,
      invitedBy,
      origin
    })
  } catch (error) {
    logger.error(error.message)
    Sentry?.captureException(error)
  }
}

/**
 * Encapsulates the logic for sending a plan invite to outsiders - add invitation to DB and send email
 */
export async function sendPlanInviteToOutsiders({ boardId, invitation, recipientName, origin }) {
  try {
    const sendEmail = firebase.functions().httpsCallable('sendPlanInviteEmailToOutsiders')

    await sendEmail({
      origin,
      planName: invitation.planName,
      invitedBy: invitation.invitedBy,
      recipient: invitation.recipient,
      recipientName
    })

    await createPlanInvitationForOutsider({ boardId, invitation })
  } catch (error) {
    logger.error(error.message)
    Sentry?.captureException(error)
  }
}
